import React, { useState } from 'react';

const VeterinarianClinicGame = ({ gameData, onComplete }) => {
    const [currentCaseIndex, setCurrentCaseIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [step, setStep] = useState('intake'); // 'intake', 'treatment'
    const [selectedDiagnosis, setSelectedDiagnosis] = useState(null);
    const [feedback, setFeedback] = useState(null); 

    const currentCase = gameData?.cases?.[currentCaseIndex];

    const handleDiagnosis = (diagnosis) => {
        if (feedback) return;
        
        if (diagnosis === currentCase.diagnosis) {
            setSelectedDiagnosis(diagnosis);
            setStep('treatment');
            if (navigator.vibrate) navigator.vibrate(60);
        } else {
            setFeedback({ 
                status: 'error', 
                title: 'DIAGNOZĖS KLAIDA 🩺', 
                message: 'Panašu, kad simptomai rodo ką kita. Pabandyk dar kartą ištirti pacientą!' 
            });
            if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
            setTimeout(() => setFeedback(null), 3000);
        }
    };

    const handleTreatment = (tool) => {
        if (feedback) return;

        if (tool === currentCase.treatment_tool) {
            const addedScore = gameData.points_per_case || 25;
            const newScore = score + addedScore;
            setScore(newScore);
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
            
            setFeedback({ 
                status: 'correct', 
                title: 'PACIENTAS PASVEIKO! 💖', 
                message: `${currentCase.name} vėl džiaugsmingai vizgina uodegą! ${currentCase.treatment_action}.` 
            });

            setTimeout(() => {
                setFeedback(null);
                if (currentCaseIndex < gameData.cases.length - 1) {
                    setCurrentCaseIndex(prev => prev + 1);
                    setStep('intake');
                    setSelectedDiagnosis(null);
                    if (navigator.vibrate) navigator.vibrate(30);
                } else {
                    if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 300]);
                    onComplete?.(newScore);
                }
            }, 5000);

        } else {
            setFeedback({ 
                status: 'error', 
                title: 'NETINKAMAS GYDYMAS 🧰', 
                message: `Šis įrankis (${tool}) nepadės gydant: ${currentCase.diagnosis}! Peržiūrėk medicininę įrangą dar kartą.` 
            });
            if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
            setTimeout(() => setFeedback(null), 3500);
        }
    };

    const diagnosesOptions = React.useMemo(() => {
        if (!currentCase) return [];
        const extraDiagnoses = [
            "Pilvo skausmas",
            "Peršalimas",
            "Danties skausmas",
            "Alergija",
            "Ausies uždegimas",
            "Letenos trauma"
        ].filter(d => d !== currentCase.diagnosis);
        
        const options = [currentCase.diagnosis, ...extraDiagnoses].slice(0, 4);
        return options.sort(() => Math.random() - 0.5);
    }, [currentCase]);

    if (!gameData || !gameData.cases || !currentCase) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-rose-100 animate-pulse bg-white">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">🏥</div>
                <h3 className="text-base md:text-3xl font-black text-rose-900 tracking-tighter uppercase">Ruošiama klinika...</h3>
            </div>
        );
    }

    const progress = (currentCaseIndex / gameData.cases.length) * 100;

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Header / Medical HUD */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-rose-50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-rose-500/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-2 md:p-6">
                        <div className="w-20 h-20 bg-rose-100 rounded-3xl flex items-center justify-center text-lg md:text-4xl shadow-inner border-2 border-rose-200 animate-float">
                            🏥
                        </div>
                        <div>
                            <h3 className="text-lg md:text-4xl font-black text-slate-800 tracking-tighter leading-tight uppercase">
                                Gyvūnų <span className="text-rose-600">Gydytojas</span>
                            </h3>
                            <p className="text-slate-500 font-bold italic font-serif">
                                Ištirk simptomus, nustatyk diagnozę ir išgydyk mažuosius draugus!
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 md:p-6">
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">Prestižas</p>
                            <p className="text-base md:text-3xl font-black text-rose-600 leading-none">⭐ {score}</p>
                        </div>
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner text-center min-w-[120px]">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">Pacientas</p>
                            <p className="text-base md:text-3xl font-black text-slate-800 leading-none">{currentCaseIndex + 1}/{gameData.cases.length}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-8 w-full bg-slate-200/50 rounded-full h-4 p-1 shadow-inner overflow-hidden border-2 border-slate-300">
                    <div 
                        className="h-full bg-gradient-to-r from-rose-400 via-pink-500 to-red-600 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(244,63,94,0.4)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {/* Immersive Clinic Stage Area */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-16 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-[250px] opacity-[0.03] select-none pointer-events-none rotate-12">🩺</div>
                <div className="absolute bottom-0 left-0 p-4 md:p-12 text-[250px] opacity-[0.03] select-none pointer-events-none -rotate-12 text-rose-600">🐶</div>
                
                {/* Advanced Patient Information Card */}
                <div className="w-full flex flex-col xl:flex-row items-center gap-4 md:p-16 mb-20 relative z-10">
                    <div className="xl:w-1/3 flex justify-center">
                        <div className="text-2xl md:text-6xl md:text-[200px] lg:text-[240px] drop-shadow-[0_40px_80px_rgba(244,63,94,0.3)] animate-float">
                            {currentCase.emoji}
                        </div>
                    </div>
                    
                    <div className="flex-1 space-y-3 md:space-y-8 text-center xl:text-left w-full">
                        <div className="space-y-3">
                            <div className="inline-block bg-rose-600 text-white px-6 py-2 rounded-2xl text-[10px] font-black uppercase tracking-[0.4em] shadow-lg animate-pop-in">Medicininė Kortelė</div>
                            <h2 className="text-2xl md:text-6xl lg:text-8xl font-black text-slate-950 tracking-tighter leading-none italic font-serif">
                                {currentCase.name} <span className="text-slate-300 text-lg md:text-4xl lg:text-xl md:text-5xl not-italic font-sans">({currentCase.animal})</span>
                            </h2>
                        </div>
                        
                        <div className="bg-white rounded-2xl md:rounded-[4rem] p-4 md:p-12 lg:p-4 md:p-16 shadow-[inset_0_10px_30px_rgba(244,63,94,0.05)] border-4 border-rose-50 relative group overflow-hidden">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(244,63,94,0.02)_0%,transparent_100%)] pointer-events-none" />
                            <div className="absolute top-0 left-0 p-4 md:p-10 text-8xl text-rose-200 opacity-20 select-none group-hover:scale-125 transition-transform">📋</div>
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.5em] mb-6 relative z-10">Pastebėti Simptomai</h4>
                            <p className="text-lg md:text-4xl lg:text-2xl md:text-6xl font-black text-rose-600 leading-tight italic text-center font-serif relative z-10 drop-shadow-sm px-6">
                                "{currentCase.symptom}"
                            </p>
                        </div>
                    </div>
                </div>

                {/* Tactical Medical Interaction Panels */}
                <div className="w-full relative z-10">
                    {step === 'intake' ? (
                        <div className="animate-slide-up space-y-4 md:space-y-12">
                            <div className="space-y-4">
                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.6em] mb-4 text-center">Diagnozės Pasirinkimas</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:p-8 max-w-5xl mx-auto">
                                    {diagnosesOptions.map((diag, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => handleDiagnosis(diag)}
                                            className="premium-button-3d bg-white p-4 md:p-10 rounded-xl md:rounded-[3rem] flex items-center gap-3 md:p-8 transition-all duration-500 border-b-[12px] border-slate-100 text-left group hover:scale-[1.05] shadow-2xl transform-gpu active:translate-y-4 active:border-b-0"
                                        >
                                            <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center text-lg md:text-4xl group-hover:bg-rose-50 group-hover:scale-110 transition-all border-2 border-slate-100 shadow-inner">🩺</div>
                                            <span className="text-base md:text-3xl lg:text-lg md:text-4xl font-black text-slate-900 group-hover:text-rose-600 transition-colors uppercase tracking-tighter italic font-serif">{diag}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="animate-slide-up space-y-16">
                            <div className="flex flex-col items-center gap-2 md:p-6 text-center animate-pop-in">
                                <div className="bg-emerald-600 text-white px-10 py-3 rounded-full text-[10px] font-black uppercase tracking-[0.5em] shadow-xl border-4 border-white/20">Patvirtinta Diagnozė</div>
                                <h3 className="text-2xl md:text-6xl lg:text-8xl font-black text-emerald-800 tracking-tighter italic font-serif leading-none drop-shadow-sm">{currentCase.diagnosis}</h3>
                            </div>
                            
                            <div className="space-y-4 md:space-y-10">
                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.6em] mb-4 text-center">Gydymo Procedūra: Įrankiai</h4>
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:p-8 max-w-6xl mx-auto">
                                    {gameData.tools.map((tool, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => handleTreatment(tool)}
                                            className="premium-button-3d bg-white p-4 md:p-10 rounded-2xl md:rounded-[4rem] flex flex-col items-center justify-center gap-3 md:p-8 transition-all duration-500 border-b-[16px] border-slate-100 group shadow-2xl transform-gpu hover:scale-105 active:translate-y-4 active:border-b-0"
                                        >
                                            <div className="text-8xl group-hover:scale-125 transition-transform drop-shadow-xl animate-float">🧰</div>
                                            <span className="text-xl lg:text-2xl font-black text-slate-900 text-center leading-tight group-hover:text-emerald-600 transition-colors uppercase tracking-widest">{tool}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Immersive Medical Feedback Overlay */}
            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[1000] p-2 md:p-6">
                    <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-3xl animate-fade-in" />
                    <div className={`
                        max-w-3xl w-full p-4 md:p-20 rounded-3xl md:rounded-[5rem] bg-white shadow-[0_60px_120px_rgba(0,0,0,0.6)] relative animate-pop-in border-[16px] text-center
                        ${feedback.status === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-48 h-48 bg-white rounded-full flex items-center justify-center text-xl md:text-5xl md:text-[120px] shadow-2xl border-8 border-inherit animate-bounce">
                            {feedback.status === 'correct' ? '🐶' : '🚑'}
                        </div>
                        
                        <div className="text-center space-y-4 md:space-y-12 pt-14">
                            <h2 className={`text-7xl font-black uppercase tracking-tighter leading-none italic font-serif ${feedback.status === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback.title}
                            </h2>

                            <div className={`p-4 md:p-12 rounded-2xl md:rounded-[4rem] border-4 text-center italic font-serif leading-relaxed ${feedback.status === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-lg md:text-4xl font-bold text-slate-800 leading-relaxed italic tracking-tight px-6">
                                    {feedback.message}
                                </p>
                            </div>

                            <div className="flex items-center justify-center gap-2 md:p-6">
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce"></div>
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VeterinarianClinicGame;
